import { IconSelector, IconX } from "@tabler/icons-react";
import clsx from "clsx";
import {
	type ClearIndicatorProps,
	type DropdownIndicatorProps,
	type MenuPlacement,
	type MultiValue,
	type MultiValueRemoveProps,
	type OptionProps,
	components,
} from "react-select";
import CreatableSelectBase from "react-select/creatable";

type SelectOption = {
	value?: string | number | null;
	label: string;
};

type MultiCreatableValue = SelectOption["value"];

type Props = {
	placeholder?: string;
	className?: string;
	clearable?: boolean;
	disabled?: boolean;
	options: SelectOption[];
	minHeight?: number;
	maxHeight?: number;
	value?: MultiCreatableValue[];
	onValueChange?: (values: MultiCreatableValue[]) => void;
	isValidNewOption?: (input: string) => boolean;
	formatCreateLabel?: (input: string) => string;
};

const ClearIndicator = (props: ClearIndicatorProps<SelectOption, true>) => (
	<components.ClearIndicator {...props}>
		<IconX size={18} stroke={1.5} />
	</components.ClearIndicator>
);

const DropdownIndicator = (props: DropdownIndicatorProps<SelectOption, true>) => (
	<components.DropdownIndicator {...props}>
		<IconSelector size={18} stroke={1.5} />
	</components.DropdownIndicator>
);

const MultiValueRemove = (props: MultiValueRemoveProps<SelectOption>) => (
	<components.MultiValueRemove {...props}>
		<IconX size={14} stroke={1.5} />
	</components.MultiValueRemove>
);

const Option = (props: OptionProps<SelectOption, true>) => <components.Option {...props} />;

export default function MultiCreatableSelect({
	placeholder,
	clearable = true,
	minHeight = 300,
	maxHeight = 400,
	...props
}: Props) {
	const values = Array.isArray(props.value) ? props.value : [];
	const selected: SelectOption[] = values.map((v) => {
		const normalized: SelectOption["value"] = v ?? null;
		const match = props.options.find((option) => option.value === normalized);
		return match ?? { value: normalized, label: normalized != null ? String(normalized) : "" };
	});

	const optionalProps: {
		isValidNewOption?: (input: string) => boolean;
		formatCreateLabel?: (input: string) => string;
	} = {};
	if (props.isValidNewOption) optionalProps.isValidNewOption = props.isValidNewOption;
	if (props.formatCreateLabel) optionalProps.formatCreateLabel = props.formatCreateLabel;

	return (
		<CreatableSelectBase
			isMulti
			components={{ ClearIndicator, DropdownIndicator, MultiValueRemove, Option }}
			placeholder={placeholder}
			value={selected}
			options={props.options}
			isClearable={clearable}
			isDisabled={props.disabled}
			minMenuHeight={minHeight}
			maxMenuHeight={maxHeight}
			menuPlacement={"auto" as MenuPlacement}
			closeMenuOnSelect={false}
			{...optionalProps}
			onChange={(selectedOptions: MultiValue<SelectOption>) => {
				if (props.onValueChange) {
					props.onValueChange(selectedOptions.map((option) => option.value ?? null));
				}
			}}
			classNames={{
				control: ({ isDisabled }) =>
					clsx(
						"flex flex-row w-full min-h-[38px] pl-3 pr-0 gap-3 text-base !rounded-md",
						isDisabled
							? "!bg-muted !border-border !text-muted-foreground !cursor-not-allowed"
							: "!bg-background !border-border focus-within:!border-focus-border focus-within:!ring-2 focus-within:!ring-focus-ring/30 !ring-offset-0 !outline-0",
					),
				valueContainer: () => "!p-0",
				placeholder: () => "!text-muted-foreground",
				menu: () => "!mt-1 !bg-background !border-1 !border-border !rounded-md !z-[90]",
				option: ({ isFocused, isSelected }) =>
					clsx(
						"!px-3 !py-2 !text-base !text-foreground !cursor-pointer",
						isSelected ? "!bg-blue-100 !text-foreground" : isFocused ? "!bg-blue-50" : "!text-foreground",
					),
				multiValue: () => "inline-flex items-center !h-[24px] !max-h-[24px] !p-1 !rounded-md !bg-blue-100",
				multiValueLabel: () => "!text-sm !text-blue-700",
				multiValueRemove: () => "!text-sm !text-muted-foreground hover:!text-foreground !bg-transparent cursor-pointer",
				dropdownIndicator: () => "!text-muted-foreground",
				clearIndicator: () => "!text-muted-foreground !cursor-pointer",
			}}
			className={clsx("w-full text-base", props.className)}
		/>
	);
}
